import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import InstallPrompt from "@/components/InstallPrompt";
import Welcome from "@/pages/Welcome";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Consent from "@/pages/Consent";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Trips from "@/pages/Trips";
import NewTrip from "@/pages/NewTrip";
import Report from "@/pages/Report";
import Accessibility from "@/pages/Accessibility";
import Emergency from "@/pages/Emergency";
import Profile from "@/pages/Profile";
import ProfileEdit from "@/pages/ProfileEdit";
import MedicalCard from "@/pages/MedicalCard";
import CareHome from "@/pages/care/CareHome";
import NewReview from "@/pages/care/NewReview";
import Cases from "@/pages/care/Cases";
import CaseDetail from "@/pages/care/CaseDetail";
import Records from "@/pages/care/Records";
import CareAccount from "@/pages/care/CareAccount";
import CareWelcome from "@/pages/care/CareWelcome";
import Specialists from "@/pages/care/Specialists";
import SpecialistDetail from "@/pages/care/SpecialistDetail";
import SpecialistSignup from "@/pages/specialist/SpecialistSignup";
import SpecialistLogin from "@/pages/specialist/SpecialistLogin";
import SpecialistStatus from "@/pages/specialist/SpecialistStatus";
import SpecialistPortal from "@/pages/specialist/SpecialistPortal";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminSpecialists from "@/pages/admin/AdminSpecialists";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AccountDeletion from "@/pages/AccountDeletion";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0C1B33] text-white">
      <div className="animate-pulse font-head text-xl">PocketDocta…</div>
    </div>
  );
}

function Protected({ children, requireProfile = true, requireConsent = true }) {
  const { user, hasProfile, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/welcome" replace />;
  if (requireConsent && !user.consent_health_data) return <Navigate to="/consent" replace />;
  if (requireProfile && !hasProfile) return <Navigate to="/onboarding" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, hasProfile, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (user) {
    if (!user.consent_health_data) return <Navigate to="/consent" replace />;
    return <Navigate to={hasProfile ? "/care" : "/onboarding"} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/welcome" element={<PublicOnly><Welcome /></PublicOnly>} />
      <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
      <Route path="/consent" element={<Protected requireProfile={false} requireConsent={false}><Consent /></Protected>} />
      <Route path="/onboarding" element={<Protected requireProfile={false}><Onboarding /></Protected>} />
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="/trips" element={<Protected><Trips /></Protected>} />
      <Route path="/trips/new" element={<Protected><NewTrip /></Protected>} />
      <Route path="/trips/:tripId/report" element={<Protected><Report /></Protected>} />
      <Route path="/accessibility" element={<Protected><Accessibility /></Protected>} />
      <Route path="/emergency" element={<Protected><Emergency /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/profile/edit" element={<Protected><ProfileEdit /></Protected>} />
      <Route path="/medical-card" element={<Protected><MedicalCard /></Protected>} />
      <Route path="/care" element={<Protected><CareHome /></Protected>} />
      <Route path="/care/welcome" element={<Protected><CareWelcome /></Protected>} />
      <Route path="/care/specialists" element={<Protected><Specialists /></Protected>} />
      <Route path="/care/specialists/:specialistId" element={<Protected><SpecialistDetail /></Protected>} />
      <Route path="/care/new" element={<Protected><NewReview /></Protected>} />
      <Route path="/care/cases" element={<Protected><Cases /></Protected>} />
      <Route path="/care/cases/:caseId" element={<Protected><CaseDetail /></Protected>} />
      <Route path="/care/records" element={<Protected><Records /></Protected>} />
      <Route path="/care/account" element={<Protected><CareAccount /></Protected>} />
      {/* Specialist portal (own auth via pd_sp_token) */}
      <Route path="/specialist/signup" element={<SpecialistSignup />} />
      <Route path="/specialist/login" element={<SpecialistLogin />} />
      <Route path="/specialist/status" element={<SpecialistStatus />} />
      <Route path="/specialist/portal" element={<SpecialistPortal />} />
      {/* Admin verification (patient token gated by ADMIN_EMAILS) */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminSpecialists />} />
      {/* Public legal pages (accessible signed-in or out) */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/account-deletion" element={<AccountDeletion />} />
      <Route path="*" element={<Navigate to="/care" replace />} />
    </Routes>
  );
}

function Root() {
  const location = useLocation();
  // Handle the Google OAuth return (session_id in URL fragment) before anything else.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <AppRoutes />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Root />
        <InstallPrompt />
        <Toaster position="top-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
